import styles from '../CompleteBuilding/CompleteBuilding.module.css';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useFormik } from 'formik';
import clsx from 'clsx';
import {
  getBuildingDetailsById,
  getTenantDetails,
  markIDVApplicationSubmitted,
  updateBuilding,
} from '../../../auth/_requests';
import { useAuth } from '../../../auth/Auth';
import { ArrowRight, ChevronLeft, Pencil, X } from 'lucide-react';
import { useModal } from '../../hooks/modalContext';
import { FetchAddress } from '../../utils/utils';
import { Dollar } from '../../../pages/addProperty/AddProperty/AddProperty';
import { Button, Tooltip, Whisper } from 'rsuite';
import infoIcon from '../../../assets/pages/addProperty/rentalInfo/infoIcon.svg';
import { rentAddressScehama } from '../../utils/addPropertyScehmas/rentAddressSchema';
import { showToast } from '../../utils/showToast';
import { DueDateCalendar } from '../../components/DueDateCalendar/DueDateCalendar';

const initialValuesRNT = {
  rent: null as number | null,
  dueDay: 1,
  address: '',
  unitNo: '',
  street: '',
  house: '',
  city: '',
  province: '',
  provinceCode: '',
  postal: '',
  country: 'Canada',
};

const AddBuildingAddress = () => {
  const { auth } = useAuth();
  const { setModal, modalState } = useModal();
  const navigate = useNavigate();
  const addressInputRef = useRef(null);
  const [isApplication, setIsApplication] = useState(false);
  const [isNewIdvRequired, setIsNewIdvRequired] = useState(false);
  const [tenantIDV, setTenantIDV] = useState<number | null>(null);
  const [dueDay, setDueDay] = useState<number>(1);
  const [formattedRent, setFormattedRent] = useState<string>('');
  const [loadingBtn, setLoadingBtn] = useState(false);
  const [isManualAddress, setIsManualAddress] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [isAutoCompleteOpen, setIsAutocompleteOpen] = useState(false);
  const tenantBuildingId = modalState?.tenantBuildingId;
  const propertyPrefill = modalState?.propertyPrefill;
  const formik = useFormik({
    initialValues: initialValuesRNT,
    validationSchema: rentAddressScehama,
    validateOnMount: true,
    onSubmit: async (values) => {
      try {
        setLoadingBtn(true);
        const requestBody = {
          buildingDetails: {
            rentAmount: Number(values.rent) || 0,
            dueDate: values.dueDay,
          },
          addressDetails: {
            streetName: values.street,
            city: values.city,
            province: values.province,
            provinceCode: values.provinceCode,
            country: 'Canada',
            countryCode: 'CA',
            postalCode: values.postal,
            fullAddress: values.address,
            buildingNumber: values.house,
            unitNumber: values.unitNo,
          },
        };
        await updateBuilding(auth?.data.token, tenantBuildingId, requestBody);
        if (isApplication) {
          try {
            await markIDVApplicationSubmitted(auth?.data.token, String(tenantIDV));
            if (isNewIdvRequired) {
              // Admin requested both application changes AND a new IDV session —
              // proceed straight to identity verification after submission.
              setModal('identity-verification', {
                tenantBuildingId,
                refreshProfile: true,
              });
            } else {
              showToast({
                message: 'Application updated successfully!',
                type: 'success',
              });
              setTimeout(() => {
                setModal(null);
              }, 5000);
            }
          } catch {
            console.error('Error submitting application');
          }
        } else {
          showToast({ message: 'Home details updated successfully.', type: 'success' });
          setModal(null, {
            refreshProperties: true,
            refreshProfile: true,
          });
          setLoadingBtn(false);
          navigate('/pay-rent', {
            state: { buildingId: tenantBuildingId, initialStep: 1 },
          });
        }
      } catch {
        console.error('Error updating building');
        setLoadingBtn(false);
      }
    },
  });
  useEffect(() => {
    const fetchBuildingDetails = async () => {
      try {
        setFetchingData(true);
        const response = await getBuildingDetailsById(auth?.data.token, tenantBuildingId);
        const data = response.data[0];

        const rent = propertyPrefill?.rent ?? data.building.rentAmount;
        const due = data.building.dueDate;

        setDueDay(due);
        setFormattedRent(Number(rent || 0).toLocaleString());

        const hasPrefillAddress = Boolean(
          propertyPrefill?.address || propertyPrefill?.street || propertyPrefill?.city
        );
        if (hasPrefillAddress) {
          setIsManualAddress(true);
        }

        formik.setValues({
          rent: rent,
          dueDay: due,
          address: propertyPrefill?.address || data.address?.fullAddress || '',
          unitNo: propertyPrefill?.unitNo || data.address?.unitNumber || '',
          street: propertyPrefill?.street || data.address?.streetName || '',
          house: propertyPrefill?.house || data.address?.buildingNumber || '',
          city: propertyPrefill?.city || data.address?.city || '',
          province: propertyPrefill?.province || data.address?.province || '',
          provinceCode: propertyPrefill?.provinceCode || data.address?.provinceCode || '',
          postal: propertyPrefill?.postal || data.address?.postalCode || '',
          country: propertyPrefill?.country || data.address?.country || initialValuesRNT.country,
        });
      } catch (error) {
        console.error('Error fetching details of a building', error);
      } finally {
        setFetchingData(false);
      }
    };
    fetchBuildingDetails();
  }, [auth?.data.token, tenantBuildingId, propertyPrefill]);

  const handleRentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;
    value = value.replace(/[^0-9.]/g, '');
    if (value && value !== '0') {
      const parts = value.split('.');
      if (parts.length === 2) {
        setFormattedRent(`${parseInt(parts[0], 10).toLocaleString()}.${parts[1]}`);
      } else {
        setFormattedRent(parseInt(value, 10).toLocaleString());
      }
      formik.setFieldValue('rent', value);
    } else {
      setFormattedRent(value);
      formik.setFieldValue('rent', value);
    }
  };
  const hasErrors = Object.keys(formik.errors).length > 0;
  const isDisabled = hasErrors || !formik.dirty || loadingBtn || fetchingData;
  useEffect(() => {
    if (!formik.touched.address || !formik.values.address?.trim()) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      const missingRequiredAddressFields =
        !formik.values.city?.trim() ||
        !formik.values.province?.trim() ||
        !formik.values.postal?.trim();

      if (missingRequiredAddressFields) {
        setIsManualAddress(true);
      }
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [
    formik.touched.address,
    formik.values.address,
    formik.values.city,
    formik.values.province,
    formik.values.postal,
  ]);

  useEffect(() => {
    const fetchIDVAddress = async () => {
      try {
        setFetchingData(true);
        const response = await getTenantDetails(auth?.data.token);
        if (response.data.tenant.tenantIdv)
          setTenantIDV(response.data.tenant?.tenantIdv.tenantIdvId);
        if (
          response.data.tenant.tenantIdv &&
          response.data.tenant.tenantIdv.status === 'changeRequest'
        ) {
          // Always mark as application-change-request flow when status is changeRequest.
          // isNewIdvRequired only controls whether IDV is launched *after* submission.
          setIsApplication(true);
          setIsNewIdvRequired(Boolean(response.data.tenant.tenantIdv.isNewIdvRequired));
        }
      } catch {
        console.error('Error fetching tenant details');
      } finally {
        setFetchingData(false);
      }
    };
    fetchIDVAddress();
  }, []);

  return (
    <>
      <motion.main
        className={`${styles.main} ${styles.main2} ${isAutoCompleteOpen && 'overflow-hidden'}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {!fetchingData && (
          <FetchAddress
            addressInputRef={addressInputRef}
            formik={formik}
            isSaveManually={setIsManualAddress}
            setIsAutocompleteOpen={setIsAutocompleteOpen}
          />
        )}

        <X className={styles.crossIcon} size={26} onClick={() => setModal(null)} stroke='#0E0E0B' />

        {isManualAddress ? (
          <div className={styles.headingOuter}>
            <button onClick={() => setIsManualAddress(false)}>
              <ChevronLeft stroke='#0E0E0B' />
            </button>
            <div className={`${styles.heading}`}>Enter address manually</div>
          </div>
        ) : (
          <div className={styles.headingOuter}>
            <button
              onClick={() => setModal('complete-building', { tenantBuildingId: tenantBuildingId })}
            >
              <ChevronLeft stroke='#0E0E0B' className='h-7 w-7' />
            </button>
            <div className={`${styles.heading}`}>Property details</div>
          </div>
        )}
        <motion.div
          className={styles.padding}
          key='rent'
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
        >
          <form onSubmit={formik.handleSubmit} className='flex flex-col gap-[18px]'>
            {' '}
            <div className={`${styles.inputOuter2} inputWrapper `}>
              <div className={styles.labelOuter}>
                <label className='label'>
                  Rental property address<span className={styles.imp}>*</span>
                </label>
                {!isManualAddress && (
                  <button
                    className={styles.editOuter}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setIsManualAddress(!isManualAddress);
                    }}
                  >
                    <Pencil width={20} height={20} />
                    <span>Enter manually</span>
                  </button>
                )}
              </div>
              {fetchingData ? (
                <div className='inputFieldLoading' />
              ) : (
                <input
                  type={'text'}
                  ref={addressInputRef}
                  autoComplete='off'
                  {...formik.getFieldProps('address')}
                  placeholder='Enter your full address'
                  className={clsx(
                    formik.touched.address && formik.errors.address ? styles.isInvalid : '',
                    formik.touched.address && !formik.errors.address ? styles.valid : '',
                    'inputField'
                  )}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                    }
                  }}
                />
              )}
              {formik.touched.address && formik.errors.address && (
                <div className={styles.error}>
                  <span>{formik.errors.address}</span>
                </div>
              )}
            </div>
            {isManualAddress ? (
              <>
                <div className={styles.fieldCol}>
                  <div className={`${styles.fieldOuter} inputWrapper`}>
                    <div className={styles.labelOuter}>
                      <label className='label'>Building or street number</label>
                    </div>
                    {fetchingData ? (
                      <div className='inputFieldLoading' />
                    ) : (
                      <input
                        type={'text'}
                        autoComplete='off'
                        {...formik.getFieldProps('house')}
                        placeholder='Enter your building/street number'
                        className={clsx(
                          formik.touched.house && formik.errors.house && formik.values.house !== ''
                            ? styles.isInvalid
                            : '',
                          formik.touched.house && !formik.errors.house && formik.values.house !== ''
                            ? styles.valid
                            : '',
                          'inputField'
                        )}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                          }
                        }}
                      />
                    )}
                    {formik.touched.house && formik.errors.house && (
                      <div className={styles.error}>
                        <span>{formik.errors.house}</span>
                      </div>
                    )}
                  </div>
                  <div className={`${styles.fieldOuter} inputWrapper`}>
                    <div className={styles.labelOuter}>
                      <label className='label'>Unit number</label>
                    </div>
                    {fetchingData ? (
                      <div className='inputFieldLoading' />
                    ) : (
                      <input
                        type={'text'}
                        autoComplete='off'
                        {...formik.getFieldProps('unitNo')}
                        placeholder='Enter your unit number'
                        className={clsx(
                          formik.touched.unitNo &&
                            formik.errors.unitNo &&
                            formik.values.unitNo !== ''
                            ? styles.isInvalid
                            : '',
                          formik.touched.unitNo &&
                            !formik.errors.unitNo &&
                            formik.values.unitNo !== ''
                            ? styles.valid
                            : '',
                          'inputField'
                        )}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                          }
                        }}
                      />
                    )}
                    {formik.touched.unitNo && formik.errors.unitNo && (
                      <div className={styles.error}>
                        <span>{formik.errors.unitNo}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className={styles.fieldCol}>
                  <div className={`${styles.fieldOuter} inputWrapper`}>
                    <div className={styles.labelOuter}>
                      <label className='label'>
                        Street name<span className={styles.imp}>*</span>
                      </label>
                    </div>
                    {fetchingData ? (
                      <div className='inputFieldLoading' />
                    ) : (
                      <input
                        type={'text'}
                        autoComplete='off'
                        {...formik.getFieldProps('street')}
                        placeholder='Enter your street name'
                        className={clsx(
                          formik.touched.street && formik.errors.street ? styles.isInvalid : '',
                          formik.touched.street && !formik.errors.street ? styles.valid : '',
                          'inputField'
                        )}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                          }
                        }}
                      />
                    )}
                    {formik.touched.street && formik.errors.street && (
                      <div className={styles.error}>
                        <span>{formik.errors.street}</span>
                      </div>
                    )}
                  </div>
                  <div className={`${styles.fieldOuter} inputWrapper`}>
                    <div className={styles.labelOuter}>
                      <label className='label'>
                        City<span className={styles.imp}>*</span>
                      </label>
                    </div>
                    {fetchingData ? (
                      <div className='inputFieldLoading' />
                    ) : (
                      <input
                        type={'text'}
                        autoComplete='off'
                        {...formik.getFieldProps('city')}
                        placeholder='Enter your city'
                        className={clsx(
                          formik.touched.city && formik.errors.city ? styles.isInvalid : '',
                          formik.touched.city && !formik.errors.city ? styles.valid : '',
                          'inputField'
                        )}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                          }
                        }}
                      />
                    )}
                    {formik.touched.city && formik.errors.city && (
                      <div className={styles.error}>
                        <span>{formik.errors.city}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className={styles.fieldCol}>
                  <div className={`${styles.fieldOuter} inputWrapper`}>
                    <div className={styles.labelOuter}>
                      <label className='label'>
                        Province<span className={styles.imp}>*</span>
                      </label>
                    </div>
                    {fetchingData ? (
                      <div className='inputFieldLoading' />
                    ) : (
                      <input
                        type={'text'}
                        autoComplete='off'
                        {...formik.getFieldProps('province')}
                        placeholder='Enter your province'
                        className={clsx(
                          formik.touched.province && formik.errors.province ? styles.isInvalid : '',
                          formik.touched.province && !formik.errors.province ? styles.valid : '',
                          'inputField'
                        )}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                          }
                        }}
                      />
                    )}
                    {formik.touched.province && formik.errors.province && (
                      <div className={styles.error}>
                        <span>{formik.errors.province}</span>
                      </div>
                    )}
                  </div>
                  <div className={`${styles.fieldOuter} inputWrapper`}>
                    <div className={styles.labelOuter}>
                      <label className='label'>
                        Postal code<span className={styles.imp}>*</span>
                      </label>
                    </div>
                    {fetchingData ? (
                      <div className='inputFieldLoading' />
                    ) : (
                      <input
                        type={'text'}
                        autoComplete='off'
                        {...formik.getFieldProps('postal')}
                        placeholder='Enter postal code'
                        className={clsx(
                          formik.touched.postal && formik.errors.postal ? styles.isInvalid : '',
                          formik.touched.postal && !formik.errors.postal ? styles.valid : '',
                          'inputField'
                        )}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                          }
                        }}
                      />
                    )}
                    {formik.touched.postal && formik.errors.postal && (
                      <div className={styles.error}>
                        <span>{formik.errors.postal}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className={styles.fieldCol}>
                  <div className={`${styles.fieldOuter} inputWrapper`}>
                    <div className={styles.labelOuter}>
                      <label className='label'>
                        Province code<span className={styles.imp}>*</span>
                      </label>
                    </div>
                    {fetchingData ? (
                      <div className='inputFieldLoading' />
                    ) : (
                      <input
                        type={'text'}
                        autoComplete='off'
                        {...formik.getFieldProps('provinceCode')}
                        placeholder='Enter your province Code'
                        className={clsx(
                          formik.touched.provinceCode && formik.errors.provinceCode
                            ? styles.isInvalid
                            : '',
                          formik.touched.provinceCode && !formik.errors.provinceCode
                            ? styles.valid
                            : '',
                          'inputField'
                        )}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                          }
                        }}
                      />
                    )}
                    {formik.touched.provinceCode && formik.errors.provinceCode && (
                      <div className={styles.error}>
                        <span>{formik.errors.provinceCode}</span>
                      </div>
                    )}
                  </div>
                  <div className={`${styles.fieldOuter} inputWrapper`}>
                    <label className={'label'}>
                      Country<span className={styles.imp}>*</span>
                    </label>
                    {fetchingData ? (
                      <div className='inputFieldLoading' />
                    ) : (
                      <div className={`${styles.countryName} inputField`}>Canada</div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className={`inputWrapper ${styles.fieldOuter}`}>
                  <label className={'label'}>
                    Monthly rent amount<span className={styles.imp}>*</span>
                  </label>
                  {fetchingData ? (
                    <div className='inputFieldLoading' />
                  ) : (
                    <div
                      className={clsx(
                        formik.touched.rent && formik.errors.rent ? styles.isInvalid : '',
                        formik.touched.rent && !formik.errors.rent ? styles.valid : '',
                        `inputField`
                      )}
                    >
                      {formik.values.rent && <Dollar />}

                      <input
                        type='text'
                        autoComplete='off'
                        {...formik.getFieldProps('rent')}
                        value={
                          formattedRent ||
                          (formik.values.rent !== null ? formik.values.rent.toLocaleString() : '')
                        }
                        placeholder='Enter your monthly rent'
                        onChange={handleRentChange}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                          }
                        }}
                      />
                    </div>
                  )}
                  {formik.touched.rent && formik.errors.rent && (
                    <div className={styles.error}>
                      <span>{formik.errors.rent}</span>
                    </div>
                  )}
                </div>
                <div className={clsx('inputWrapper', styles.dueDate, styles.fieldOuter)}>
                  <div className={styles.tooltipWrap}>
                    <label className={'label'}>
                      Rent due day each month<span className={styles.imp}>*</span>
                    </label>
                    <Whisper
                      trigger='hover'
                      placement={'right'}
                      controlId={`control-id-top`}
                      speaker={
                        <Tooltip>
                          <p>
                            Select a day between the 1st and 28th, your landlord will receive the
                            rent payment by this day each month
                          </p>
                        </Tooltip>
                      }
                    >
                      <div className={'signUpBtn'}>
                        <Button appearance='subtle' className={styles.btnImg}>
                          <img src={infoIcon} className={styles.infoIcon} alt='info' />
                        </Button>
                      </div>
                    </Whisper>
                  </div>
                  {fetchingData ? (
                    <div className='inputFieldLoading' />
                  ) : (
                    <DueDateCalendar
                      selectedDay={dueDay}
                      onSelectDay={(day) => {
                        setDueDay(day);
                        formik.setFieldValue('dueDay', day);
                      }}
                    />
                  )}
                  {formik.touched.dueDay && formik.errors.dueDay && (
                    <div className={styles.error}>
                      <span>{formik.errors.dueDay}</span>
                    </div>
                  )}
                </div>
              </>
            )}
            <button
              className={clsx(
                styles.nextBtn,
                styles.lessMargin,
                isManualAddress && styles.lessMarginBtn,
                !isDisabled && styles.submitBtn
              )}
              disabled={isDisabled}
              type='submit'
            >
              <span>{loadingBtn ? 'Please wait' : 'Next'}</span>
              <ArrowRight stroke={isDisabled ? '#0E0E0B66' : '#fff'} />
            </button>
          </form>
        </motion.div>
      </motion.main>
    </>
  );
};
export default AddBuildingAddress;
